import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import FadeInSection from "@/components/FadeInSection";
import InlineCTA from "@/components/InlineCTA";
import LazySection from "@/components/LazySection";
import NewsletterBanner from "@/components/NewsletterBanner";
import MarketSidebar from "@/components/MarketSidebar";
import { categoryPages as hardcodedCategoryPages } from "@/data/categoryPages";
import { getArticleUrl } from "@/lib/articleUrls";
import ContentSearchBar from "@/components/ContentSearchBar";
import { useArticles } from "@/contexts/ArticlesContext";
import { getCategoryColor } from "@/lib/categoryColors";
import { useSanityCategoryPages } from "@/hooks/useSanityContent";
import { Trophy, Vote, TrendingUp, BarChart3, DollarSign, Cpu, Clapperboard, Building2, CloudSun, Users } from "lucide-react";

const iconMap: Record<string, React.ElementType> = {
  Trophy, Vote, TrendingUp, BarChart3, DollarSign, Cpu, Clapperboard, Building2, CloudSun, Users,
};

const CategoryPage = () => {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const slug = paramSlug || window.location.pathname.replace(/^\/|\/$/g, "");
  const [searchQuery, setSearchQuery] = useState("");
  const { data: sanityPages } = useSanityCategoryPages();
  const { articles: allArticles } = useArticles();

  const sanityPage = sanityPages?.find((p) => p.slug?.current === slug);
  const hardcodedPage = sanityPages === undefined ? hardcodedCategoryPages.find((p) => p.slug === slug) : null;

  const page = sanityPage
    ? {
        slug,
        name: sanityPage.title,
        icon: iconMap[sanityPage.icon ?? ""] ?? TrendingUp,
        headline: sanityPage.title,
        description: sanityPage.description ?? "",
        sections: sanityPage.sections ?? [],
        relatedArticleCategory: sanityPage.title,
        relatedMarketCategory: sanityPage.title,
      }
    : hardcodedPage;

  if (!page) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Page not found</h1>
          <Link to="/prediction-markets" className="text-primary hover:underline">Back to Prediction Markets</Link>
        </div>
      </div>
    );
  }

  const Icon = page.icon;
  const allRelated = allArticles.filter((a) => a.category === page.relatedArticleCategory);
  const relatedArticles = (searchQuery.trim()
    ? allRelated.filter((a) => {
        const q = searchQuery.toLowerCase();
        return a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q);
      })
    : allRelated
  ).slice(0, 4);

  return (
    <div className="min-h-screen bg-background">
      <SEO title={page.headline} description={page.description} canonical={`/${page.slug}`} breadcrumbs={[{ name: "Home", href: "/" }, { name: "Prediction Markets", href: "/prediction-markets" }, { name: page.headline, href: `/${page.slug}` }]} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-8">
          <InlineCTA variant="newsletter" />
        </div>
        <div className="mb-6">
          <ContentSearchBar placeholder="Search this category..." onSearch={setSearchQuery} />
        </div>

        <FadeInSection>
          <Link to="/prediction-markets" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Prediction Markets
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-foreground">{page.headline}</h1>
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl mb-10">{page.description}</p>
        </FadeInSection>

        <div className="flex gap-8">
          <div className="flex-1 min-w-0">
            {page.sections.map((section, i) => (
              <FadeInSection key={i} delay={0.05 + i * 0.03}>
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-foreground mb-3">{section.heading}</h2>
                  <p className="text-base text-muted-foreground leading-relaxed">{section.content}</p>
                </div>
              </FadeInSection>
            ))}

            {relatedArticles.length > 0 && (
              <FadeInSection delay={0.2}>
                <div className="mt-10 pt-8 border-t border-border">
                  <h2 className="text-lg font-bold text-foreground mb-4">Latest {page.name} News</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {relatedArticles.map((a) => {
                      const colors = getCategoryColor(a.category);
                      return (
                        <Link key={a.id} to={getArticleUrl(a)} className="group block rounded-lg border border-border bg-card p-4 hover:border-primary/30 transition-colors">
                          <span className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: colors.hex }}>{a.category}</span>
                          <h3 className="text-sm font-bold text-foreground mt-1 line-clamp-2 group-hover:text-primary transition-colors">{a.title}</h3>
                          <p className="text-xs text-muted-foreground mt-1">{a.date}</p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </FadeInSection>
            )}

            <div className="mt-10">
              <LazySection><NewsletterBanner /></LazySection>
            </div>
          </div>

          <div className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-24">
              <MarketSidebar count={5} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
